/* BOGO Cart Guard (shopjoga)
 * ─────────────────────────────────────────────────────────────────
 * Ensures every cart line item for a product in the BOGO COLLECTION
 * has an EVEN quantity, so the automatic "Buy 1 Get 1 Free"
 * discount always applies to full pairs.
 *
 * Two layers:
 *   1. PRE-MUTATION: wraps window.fetch to intercept /cart/change
 *      and /cart/update before the request goes out, rounding the
 *      requested quantity to the nearest even number. Works for any
 *      caller that uses fetch (native Dawn cart drawer, /cart page).
 *
 *   2. POST-MUTATION RECONCILIATION: listens for clicks/inputs on
 *      Kaching cart drawer quantity controls. After Kaching's own
 *      request completes (Kaching does NOT go through window.fetch
 *      — it captures fetch early or uses XHR), we re-fetch the cart,
 *      detect any BOGO line whose quantity ended up odd, and issue a
 *      follow-up /cart/change.js to round it up. The Kaching drawer
 *      is then refreshed via its own re-render hooks.
 *
 * Rounding:
 *   • Incrementing (new > old) → round UP (3 → 4, 5 → 6)
 *   • Decrementing (new < old) → round DOWN (3 → 2, 5 → 4)
 *   • Removal (new == 0)       → honored as-is
 *
 * Diagnostics: set `window.__bogoDebug = true` in DevTools console
 * before testing, then watch logs prefixed [bogo].
 *
 * Source of truth: the "BOGO" product tag (looked up per cart line
 * via /products/{handle}.js, which exposes `tags`).
 */
(function () {
  if (window.__bogoCartGuardInstalled) return;
  window.__bogoCartGuardInstalled = true;

  const dbg = (...args) => {
    if (window.__bogoDebug) console.log('[bogo]', ...args);
  };
  dbg('guard installed (tag-based:', (window.BOGO_TAG || 'BOGO') + ')');

  const originalFetch = window.fetch.bind(window);

  // ─── Cart cache ─────────────────────────────────────────────────
  let cartCache = null;
  function getCart(force) {
    if (cartCache && !force) return Promise.resolve(cartCache);
    return originalFetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((c) => { cartCache = c; return c; })
      .catch(() => ({ items: [] }));
  }
  function invalidateCart() { cartCache = null; }

  // ─── BOGO check (tag-based) ────────────────────────────────────
  // Source of truth: products carry the "BOGO" tag. Cart line items
  // (/cart.js) don't expose tags, so we look the product up via
  // /products/{handle}.js (which DOES include `tags`) and cache the
  // result per handle. Returns a Promise<boolean>.
  const BOGO_TAG = (window.BOGO_TAG || 'BOGO').toUpperCase();
  const tagCache = new Map(); // handle -> Promise<boolean>
  function isBogoLine(handle, productId) {
    // PRIMARY: roster built server-side from the BOGO collection
    // (window.isBogoProduct, see theme.liquid). Reliable and synchronous.
    if (typeof window.isBogoProduct === 'function' && window.isBogoProduct(handle, productId)) {
      return Promise.resolve(true);
    }
    // FALLBACK: tag from /products/{handle}.js — note this endpoint does
    // NOT reliably include `tags` on every store, which is why the roster
    // above is the source of truth.
    if (!handle) return Promise.resolve(false);
    if (tagCache.has(handle)) return tagCache.get(handle);
    const p = originalFetch('/products/' + handle + '.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((prod) => {
        if (!prod || !Array.isArray(prod.tags)) return false;
        return prod.tags.some((t) => String(t).toUpperCase() === BOGO_TAG);
      })
      .catch(() => false);
    tagCache.set(handle, p);
    return p;
  }

  function roundQuantity(newQty, oldQty) {
    const n = Number(newQty);
    if (!Number.isFinite(n) || n <= 0) return n;
    if (n % 2 === 0) return n;
    if (oldQty != null && Number(oldQty) > n) {
      return Math.max(0, n - 1);
    }
    return n + 1;
  }

  // ─── PRE-MUTATION: fetch wrap ──────────────────────────────────
  function urlPath(input) {
    try {
      if (typeof input === 'string') return input.split('?')[0];
      if (input && input.url) return String(input.url).split('?')[0];
    } catch (e) { /* ignore */ }
    return '';
  }
  function isCartMutation(url) {
    return /\/cart\/(change|update|add|clear)(\.js)?$/.test(url);
  }
  function isQuantityEdit(url) {
    return /\/cart\/(change|update)(\.js)?$/.test(url);
  }

  async function parseBody(init) {
    if (!init || !init.body) return null;
    const body = init.body;
    if (body instanceof FormData) {
      const obj = {};
      body.forEach((v, k) => { obj[k] = v; });
      return { type: 'formdata', obj };
    }
    if (body instanceof URLSearchParams) {
      const obj = {};
      body.forEach((v, k) => { obj[k] = v; });
      return { type: 'urlencoded', obj };
    }
    if (typeof body === 'string') {
      try { return { type: 'json', obj: JSON.parse(body) }; } catch (e) {
        try {
          const sp = new URLSearchParams(body);
          const obj = {};
          sp.forEach((v, k) => { obj[k] = v; });
          return { type: 'urlencoded', obj };
        } catch (e2) { return null; }
      }
    }
    return null;
  }
  function serializeBody(parsed) {
    if (parsed.type === 'formdata') {
      const fd = new FormData();
      Object.entries(parsed.obj).forEach(([k, v]) => fd.append(k, v));
      return fd;
    }
    if (parsed.type === 'urlencoded') {
      const sp = new URLSearchParams();
      Object.entries(parsed.obj).forEach(([k, v]) => sp.append(k, String(v)));
      return sp.toString();
    }
    return JSON.stringify(parsed.obj);
  }
  function findLineForChange(body, cart) {
    const items = cart.items || [];
    const lineRef = body.id || body.line;
    if (lineRef == null) return null;
    const asNum = Number(lineRef);
    if (!isNaN(asNum) && Number.isInteger(asNum) && asNum >= 1 && asNum <= items.length) {
      return items[asNum - 1];
    }
    let match = items.find((i) => i.key === lineRef);
    if (match) return match;
    match = items.find((i) => Number(i.id) === asNum || Number(i.variant_id) === asNum);
    return match || null;
  }
  async function adjustChangePayload(parsed, cart) {
    const line = findLineForChange(parsed.obj, cart);
    if (!line) return false;
    if (!(await isBogoLine(line.handle, line.product_id))) return false;
    const oldQty = Number(line.quantity);
    const newQty = Number(parsed.obj.quantity);
    if (!Number.isFinite(newQty)) return false;
    const rounded = roundQuantity(newQty, oldQty);
    if (rounded === newQty) return false;
    parsed.obj.quantity = String(rounded);
    dbg('pre-mutation change: rounded', oldQty, '→', newQty, '→', rounded, line);
    return true;
  }
  async function adjustUpdatePayload(parsed, cart) {
    const updates = parsed.obj.updates;
    if (!updates || typeof updates !== 'object') return false;
    let mutated = false;
    const items = cart.items || [];
    for (const key of Object.keys(updates)) {
      const requested = Number(updates[key]);
      if (!Number.isFinite(requested)) continue;
      let line = items.find((i) => i.key === key);
      if (!line) line = items.find((i) => String(i.id) === key || String(i.variant_id) === key);
      if (!line) continue;
      if (!(await isBogoLine(line.handle, line.product_id))) continue;
      const rounded = roundQuantity(requested, Number(line.quantity));
      if (rounded !== requested) {
        updates[key] = rounded;
        mutated = true;
      }
    }
    return mutated;
  }

  // Helper: is this body a removal (qty 0 anywhere)? If so, pass it
  // through unmodified — we never want to interfere with a delete.
  function bodyLooksLikeRemoval(parsed) {
    if (!parsed) return false;
    const q = parsed.obj.quantity;
    if (q != null && (q === 0 || q === '0' || Number(q) === 0)) return true;
    const u = parsed.obj.updates;
    if (u && typeof u === 'object') {
      const values = Object.values(u);
      // True if at least one update is 0 — safer to skip than risk
      // blocking a delete-by-update call.
      if (values.some((v) => Number(v) === 0)) return true;
    }
    return false;
  }

  window.fetch = async function (input, init) {
    const url = urlPath(input);
    if (!isCartMutation(url)) return originalFetch(input, init);

    let wasRemoval = false;
    if (isQuantityEdit(url) && init && init.body) {
      try {
        const parsed = await parseBody(init);
        if (parsed && bodyLooksLikeRemoval(parsed)) {
          wasRemoval = true;
          // Suppress reconcile around the delete so we never round-up
          // (re-add) the item being removed.
          if (reconcileTimer) clearTimeout(reconcileTimer);
          suppressReconcileUntil = Date.now() + 4000;
          dbg('removal detected in fetch — passing through, suppressing reconcile');
        } else if (parsed) {
          const cart = await getCart();
          let mutated = false;
          if (/\/cart\/change/.test(url)) mutated = await adjustChangePayload(parsed, cart);
          else if (/\/cart\/update/.test(url)) mutated = await adjustUpdatePayload(parsed, cart);
          if (mutated) init = Object.assign({}, init, { body: serializeBody(parsed) });
        }
      } catch (e) {
        console.warn('[bogo-cart-guard] adjust failed', e);
      }
    }
    const result = await originalFetch(input, init);
    invalidateCart();
    // NOTE: we deliberately do NOT auto-reconcile here. Reconcile is only
    // triggered by an explicit +/- click on a cart line (see below), so
    // page-load / navigation cart mutations can never re-round the cart.
    return result;
  };

  // ─── POST-MUTATION RECONCILE ───────────────────────────────────
  // Kaching's cart drawer captures its own fetch reference and won't
  // hit our wrap. After it completes a mutation we read the cart and
  // round up any BOGO line whose quantity ended up odd.
  let reconciling = false;
  let reconcileTimer = null;
  // While the customer is deleting a line, suppress ALL reconciles so we
  // never round-up (and thus re-add) the item they're trying to remove.
  let suppressReconcileUntil = 0;
  // Reconcile is SCOPED: only product handles the customer actively
  // touched (clicked +/- on, in the cart drawer) get rounded. This
  // prevents page-load / navigation cartUpdate events from re-rounding
  // the whole cart and accidentally doubling quantities.
  const pendingHandles = new Set();

  function scheduleReconcile(delay) {
    if (Date.now() < suppressReconcileUntil) {
      dbg('reconcile suppressed (delete in progress)');
      return;
    }
    if (pendingHandles.size === 0) {
      dbg('reconcile skipped (no pending handles)');
      return;
    }
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, delay == null ? 350 : delay);
  }
  async function reconcile() {
    if (reconciling) return;
    if (Date.now() < suppressReconcileUntil) {
      dbg('reconcile aborted (delete in progress)');
      return;
    }
    if (pendingHandles.size === 0) return;
    reconciling = true;
    try {
      const cart = await getCart(true);
      const fixes = [];
      for (const line of (cart.items || [])) {
        // ONLY touch lines the customer actively changed.
        if (!pendingHandles.has(line.handle)) continue;
        const q = Number(line.quantity);
        if (!Number.isFinite(q) || q <= 0) continue;
        if (q % 2 === 0) continue;
        // Tag-based BOGO check (async, cached).
        if (!(await isBogoLine(line.handle, line.product_id))) continue;
        // No reliable "old qty" here — bias UP (add 1) so the customer
        // never loses items they intended to add.
        fixes.push({ id: line.key, quantity: q + 1 });
      }
      pendingHandles.clear();
      if (fixes.length === 0) return;
      dbg('reconcile: rounding', fixes);
      for (const f of fixes) {
        await originalFetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify(f),
        });
      }
      invalidateCart();
      // Tell every cart UI we know about to refresh.
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bogo-cart-guard' });
        }
      } catch (e) { /* ignore */ }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      // Kaching-specific refresh event (best-effort)
      document.dispatchEvent(new CustomEvent('kaching-cart:update'));
      window.dispatchEvent(new CustomEvent('kaching-cart:refresh'));
      // Also try Kaching's own re-render if exposed
      if (window.kachingCart && typeof window.kachingCart.refresh === 'function') {
        try { window.kachingCart.refresh(); } catch (e) { /* ignore */ }
      }
    } catch (e) {
      console.warn('[bogo-cart-guard] reconcile failed', e);
    } finally {
      reconciling = false;
    }
  }

  // ─── DOM hooks: Kaching cart drawer ─────────────────────────────
  function isKachingQtyControl(el) {
    if (!el || !el.closest) return false;
    // NOTE: deliberately excludes `.kaching-cart-item__remove-btn` —
    // deletes don't need rounding, and any reconcile we schedule on
    // remove could race with Kaching's own re-render and cause weird
    // behavior (item not actually disappearing from the drawer).
    return !!el.closest(
      '.kaching-cart-item__quantity-btn,' +
      '.kaching-cart-item__quantity-input,' +
      '.kaching-cart__quantity,' +
      '[class*="kaching-cart-item__quantity"]'
    );
  }

  // Removal clicks: ignore them entirely — let Kaching handle them.
  function isKachingRemoveBtn(el) {
    if (!el || !el.closest) return false;
    return !!el.closest('.kaching-cart-item__remove-btn');
  }

  // Given a clicked control, find the owning cart line and queue its
  // product handle for reconcile (only if it's BOGO-eligible).
  function queueLineFromControl(target) {
    const lineEl = target.closest && target.closest('.kaching-cart-item, [data-product-handle]');
    if (!lineEl) {
      dbg('qty control click but no cart-item ancestor found');
      return false;
    }
    const handle = lineEl.getAttribute('data-product-handle');
    if (!handle) {
      dbg('qty control click but no data-product-handle on line');
      return false;
    }
    // Queue unconditionally; the BOGO tag check happens (async, cached)
    // inside reconcile so we don't block the click handler.
    pendingHandles.add(handle);
    dbg('queued line for reconcile', handle);
    return true;
  }

  // Capture phase so we trigger even if Kaching stops propagation
  document.addEventListener(
    'click',
    (e) => {
      // Removals: cancel any pending reconcile and suppress new ones for
      // a few seconds so we don't race Kaching's delete and re-add the
      // item by rounding it up.
      if (isKachingRemoveBtn(e.target)) {
        if (reconcileTimer) clearTimeout(reconcileTimer);
        pendingHandles.clear();
        suppressReconcileUntil = Date.now() + 4000;
        dbg('delete clicked — suppressing reconcile for 4s');
        return;
      }
      if (isKachingQtyControl(e.target)) {
        if (queueLineFromControl(e.target)) scheduleReconcile();
      }
    },
    true
  );
  document.addEventListener(
    'change',
    (e) => {
      if (isKachingRemoveBtn(e.target)) return;
      if (isKachingQtyControl(e.target)) {
        if (queueLineFromControl(e.target)) scheduleReconcile();
      }
    },
    true
  );

  // Subscribe to theme's pub/sub — but ONLY to invalidate our cart cache,
  // never to auto-reconcile. Auto-reconcile on cartUpdate fired on every
  // page navigation and re-doubled quantities.
  function trySubscribe() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
      if (data && data.source === 'bogo-cart-guard') return;
      // Only invalidate the cached cart — never auto-reconcile here.
      invalidateCart();
    });
    return true;
  }
  if (!trySubscribe()) {
    // Try again after theme JS has loaded
    document.addEventListener('DOMContentLoaded', trySubscribe, { once: true });
  }

  // Warm cache
  getCart(true);
})();
